import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/synapse/Field';
import { Modal } from '@/components/synapse/Modal';
import { PasswordInput } from '@/components/synapse/PasswordInput';
import { PwStrength } from '@/components/synapse/PwStrength';
import { useSetUserPassword } from '@/hooks/usePlatformAdmin';
import { translateApiError } from '@/lib/apiError';
import { passwordScore } from '@/lib/passwordScore';

/** Matches `setPasswordInput` on the server; a shorter one is refused there. */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * Who this dialog can act on: an id to post to and enough to name them.
 *
 * Deliberately structural rather than `PlatformUser`, because two screens reach
 * it with two different row types — the password page's search result and the
 * clinic detail page's member — and neither should have to be widened into the
 * other to share one dialog.
 */
export interface PasswordTarget {
  id: string;
  name: string;
  email: string;
}

/**
 * Set one user's password directly. No email is sent; the operator relays it out
 * of band, which is the whole point of the screen this came from.
 *
 * Extracted rather than duplicated: this is the single most sensitive control in
 * the dashboard, and a second copy is a second place for the confirm check, the
 * length floor or the "never render the typed password" rule to drift.
 */
export function SetPasswordDialog({
  user,
  onClose,
  onDone,
}: {
  user: PasswordTarget;
  onClose: () => void;
  onDone: (email: string) => void;
}) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const setUserPassword = useSetUserPassword();

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== password;
  const valid = password.length >= MIN_PASSWORD_LENGTH && confirm === password;

  const submit = async () => {
    if (!valid) return;
    try {
      await setUserPassword.mutateAsync({ userId: user.id, newPassword: password });
      onDone(user.email);
    } catch {
      // Inline below. The typed password is deliberately never interpolated into
      // any message, URL or cache key.
    }
  };

  return (
    <Modal
      title={t('platformAdmin.passwords.dialogTitle', { name: user.name })}
      description={t('platformAdmin.passwords.dialogDesc', { email: user.email })}
      onClose={onClose}
      isPending={setUserPassword.isPending}
      footer={
        <>
          <Button variant="secondary" full onClick={onClose} disabled={setUserPassword.isPending}>
            {t('common.cancel')}
          </Button>
          <Button full onClick={submit} disabled={!valid || setUserPassword.isPending}>
            {setUserPassword.isPending
              ? t('common.saving')
              : t('platformAdmin.passwords.setPassword')}
          </Button>
        </>
      }
    >
      <Field
        label={t('platformAdmin.passwords.newPassword')}
        error={
          tooShort ? t('platformAdmin.passwords.tooShort', { count: MIN_PASSWORD_LENGTH }) : undefined
        }
      >
        {(id) => (
          <PasswordInput
            id={id}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            autoFocus
          />
        )}
      </Field>
      <PwStrength score={passwordScore(password)} />
      <Field
        label={t('platformAdmin.passwords.confirmPassword')}
        error={mismatch ? t('platformAdmin.passwords.mismatch') : undefined}
      >
        {(id) => (
          <PasswordInput
            id={id}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
          />
        )}
      </Field>

      <p className="text-[12px] text-muted">{t('platformAdmin.passwords.relayHint')}</p>

      {setUserPassword.isError && (
        <div className="rounded-md bg-destructive-bg/40 px-3 py-2 text-[12.5px] text-destructive">
          {translateApiError(setUserPassword.error, {
            fallback: t('platformAdmin.passwords.failed'),
          })}
        </div>
      )}
    </Modal>
  );
}
